/**
 * "Check RAS / Consumption": open the Consumption Report in a new tab for the given meal
 * items. By default it reports on the selected date and session only (fromDate === toDate).
 * Shared by the Historical Data and Production Estimation screens.
 *
 * The report applies the "all selected items used together in a session" rule
 * (itemFilter=and), so with several items checked it shows only that date+session when all of
 * them were on the menu together. session is the selected { value, label }; omitting it (or a
 * blank value) falls back to all sessions.
 *
 * Pass `range` ({ from, to }, both YYYY-MM-DD) to report on a span instead of the single day --
 * see trailingTwoYearsBeforeSession.
 */
export const openConsumptionReport = async (date, menuItemIds, session, range) => {
  const day = String(date ?? "").slice(0, 10); // normalise any ISO timestamp to YYYY-MM-DD
  const from = range?.from ?? day;
  const to = range?.to ?? day;
  const items = menuItemIds.join(",");
  const sv = session?.value;
  const sessionParam =
    sv != null && sv !== ""
      ? `&sessionId=${encodeURIComponent(sv)}&sessionName=${encodeURIComponent(session?.label ?? "")}`
      : "";
  window.open(
    `/reports/consumption?fromDate=${from}&toDate=${to}&items=${items}${sessionParam}`,
    "_blank"
  );
};

// Local calendar date as YYYY-MM-DD. Built from the local parts rather than toISOString(), which
// reports the UTC day and so lands a day early through the early-morning hours in IST.
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// A YYYY-MM-DD string as a local Date, built from its parts. `new Date("2026-08-13")` parses as
// UTC midnight, which is the previous local day anywhere west of Greenwich.
const parseDay = (value) => {
  const [y, m, d] = String(value ?? "").slice(0, 10).split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

/**
 * The window Production Estimation reports on: the two years leading up to the session date being
 * planned, ending the day before it. The window is anchored on the session date rather than on
 * today because the screen is routinely used to plan a date that is not today -- anchoring on
 * today would report a span the planned day does not sit at the end of. The session date itself is
 * left out because the day being planned has no consumption recorded against it yet.
 *
 * Returns undefined when no date is selected, which leaves openConsumptionReport on its
 * single-day default.
 */
export const trailingTwoYearsBeforeSession = (sessionDate) => {
  const base = parseDay(sessionDate);
  if (!base) return undefined;
  const to = new Date(base);
  to.setDate(to.getDate() - 1);
  const from = new Date(base);
  from.setFullYear(from.getFullYear() - 2);
  return { from: isoDay(from), to: isoDay(to) };
};
